/**
 * Wald-Kopf des Monats je Apartment (Boards „Monat je Apartment“, „Verfügbarkeit · Handy“): Monat als
 * Titel, Modus und Übertragungsstand als Chips, Apartmentwechsel ‹ Name ›, Monat ‹ › und Heute;
 * darunter die Reiter des Bereichs. Am Handy stehen die Monatspfeile am Titel, Apartment und Heute
 * darunter. Ein Wechsel mit offenen Eingaben fragt vorher nach (Aufrufer).
 */
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { ForestTodayButton, PageHeader, forestArrow, forestGroup } from '@/shared/components';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { cn } from '@/shared/lib/cn';
import { MONTH_SHORT, monthTitle } from './monthModel';

interface Props {
  month: string;
  apartments: { id: string; name: string }[];
  apartmentId: string;
  chips?: ReactNode;
  onApartment: (id: string) => void;
  onMonth: (delta: number) => void;
  onToday: () => void;
  onTab: (tab: 'calendar' | 'channels') => void;
}

export function MonthHeader({ month, apartments, apartmentId, chips, ...props }: Props) {
  const mobile = useMediaQuery('(max-width: 767px)');
  const index = apartments.findIndex((apartment) => apartment.id === apartmentId);
  const step = (delta: number) => {
    const next = apartments[(index + delta + apartments.length) % apartments.length];
    if (next) props.onApartment(next.id);
  };
  const arrow = (label: string, onClick: () => void, Icon: typeof ChevronLeft) => (
    <button type="button" aria-label={label} onClick={onClick} className={forestArrow}>
      <Icon size={18} aria-hidden />
    </button>
  );
  const apartmentSwitch = (
    <div className={cn(forestGroup, 'h-11 min-w-0', mobile && 'grow')}>
      {/* Am Handy reicht die Auswahl selbst; die Pfeile kosteten den Platz für den Namen. */}
      {!mobile && arrow('Vorheriges Apartment', () => step(-1), ChevronLeft)}
      <select
        aria-label="Apartment"
        value={apartmentId}
        onChange={(event) => props.onApartment(event.target.value)}
        className="[&>option]:text-ink h-11 min-w-0 grow appearance-none truncate bg-transparent px-3 text-sm font-bold text-white outline-none md:max-w-[11rem] md:px-1"
      >
        {apartments.map((apartment) => (
          <option key={apartment.id} value={apartment.id}>
            {apartment.name}
          </option>
        ))}
      </select>
      {mobile && <ChevronDown size={14} aria-hidden className="pointer-events-none -ml-6 mr-3" />}
      {!mobile && arrow('Nächstes Apartment', () => step(1), ChevronRight)}
    </div>
  );
  // Am Handy reicht ‹ ›: der Monat steht im Titel.
  const monthSwitch = (
    <div className={cn(forestGroup, 'h-11 flex-none')}>
      {arrow('Vorheriger Monat', () => props.onMonth(-1), ChevronLeft)}
      {!mobile && (
        <span className="px-1 text-sm font-bold">{MONTH_SHORT[Number(month.slice(5, 7)) - 1]}</span>
      )}
      {arrow('Nächster Monat', () => props.onMonth(1), ChevronRight)}
    </div>
  );
  return (
    <PageHeader
      className={mobile ? 'pb-[70px]' : 'pb-[84px] md:pb-[84px]'}
      title={monthTitle(month)}
      description={chips}
      tools={
        mobile ? undefined : (
          <>
            {apartmentSwitch}
            {monthSwitch}
            <ForestTodayButton onToday={props.onToday} />
          </>
        )
      }
      below={
        mobile ? (
          <div className="flex items-center gap-2">
            {apartmentSwitch}
            {monthSwitch}
            <ForestTodayButton onToday={props.onToday} className="px-3" />
          </div>
        ) : undefined
      }
      // Am Handy führt die Karte unter dem Monat zu den Kanalregeln (Board „Verfügbarkeit · Handy“).
      filterTabs={
        mobile
          ? undefined
          : [
              { label: 'Grundkalender', active: true, onSelect: () => props.onTab('calendar') },
              { label: 'Kanalregeln', active: false, onSelect: () => props.onTab('channels') },
            ]
      }
      filterTabsKind="tabs"
      filterTabsLabel="Bereich"
    />
  );
}
